'use client';

import { Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';

export default function PricingCard({
  title,
  price,
  priceDetails,
  tagline,
  description,
  features = [],
  textContent,
  showCTA = false,
  ctaText,
  onCTA,
  variant = 'free',
  loading = false,
  isCurrentPlan = false,
}) {
  const isPremium = variant === 'premium';
  const isDaily = variant === 'daily';
  const isFree = variant === 'free';

  // Parse price to separate amount and period
  const parsePrice = (priceStr) => {
    if (priceStr === 'Free') {
      return { amount: 'Free', period: null };
    }
    // Match patterns like "$13.99 per month" or "$4.99 per day"
    const match = priceStr.match(/^(\$[\d.]+)\s+(per\s+\w+)$/i);
    if (match) {
      return { amount: match[1], period: match[2] };
    }
    return { amount: priceStr, period: null };
  };

  const { amount, period } = parsePrice(price);

  return (
    <div
      className={`
        bg-gradient-to-br from-[#18122b] via-[#2a1a4d] to-[#6A3683]/80 
        rounded-2xl border shadow-xl p-6 md:p-8 
        flex flex-col w-full
        ${isPremium ? 'border-[#A45DE8] shadow-[0_0_24px_#A45DE8]/30' : 'border-[#6A3683]/40'}
        relative overflow-hidden
      `}
      style={
        isPremium 
          ? { transform: 'scale(1.05)', transformOrigin: 'top center' } 
          : isDaily 
          ? { transform: 'scale(0.9)', transformOrigin: 'top center' } 
          : {}
      }
    >
      {/* Background glow effect */}
      <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none">
        <div className="absolute top-0 right-0 w-60 h-60 bg-[#A45DE8] rounded-full blur-[80px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-[#D4B6FF] rounded-full blur-[80px] translate-y-1/2 -translate-x-1/4"></div>
      </div>

      <div className="relative z-10 flex flex-col h-full">
        {/* Title */}
        <h2
          className={`text-2xl md:text-3xl font-marcellus mb-2 ${
            isPremium ? 'text-[#D4B6FF]' : 'text-[#FBFBFE]'
          }`}
          style={{ fontFamily: 'var(--font-marcellus), serif' }}
        >
          {title}
        </h2>

        {/* Price */}
        <div className="mb-2">
          <div className="flex items-end gap-2">
            <span
              className={`text-4xl md:text-5xl font-bold ${
                isFree ? 'text-[#A45DE8]' : 'text-[#D4B6FF]'
              }`}
            >
              {amount}
            </span>
            {period && (
              <span className="text-sm md:text-base text-[#FBFBFE]/70 pb-1">
                {period}
              </span>
            )}
          </div>
          {priceDetails && (
            <div className="mt-2 text-sm text-[#FBFBFE]/70">
              <div>{priceDetails}</div>
            </div>
          )}
        </div>

        {/* Tagline */}
        {tagline && (
          <p className="text-sm text-[#FBFBFE]/60 italic mb-2">{tagline}</p>
        )}

        {/* Description */}
        {description && (
          <p className="text-[#FBFBFE]/80 mb-4 text-sm">{description}</p>
        )}

        {/* Text Content (for Daily Blessing) */}
        {textContent && (
          <p className="text-[#FBFBFE]/80 mb-4 text-sm leading-relaxed">{textContent}</p>
        )}

        {/* CTA Button - Between description and features */}
        {showCTA && (
          <div className="mb-6">
            <Button
              type="button"
              onClick={onCTA}
              loading={loading}
              disabled={loading || isCurrentPlan}
              variant={isPremium ? 'primary' : isCurrentPlan ? 'ghost' : 'secondary'}
              className={`w-full py-3 px-6 ${
                isPremium ? 'bg-[#A45DE8] hover:bg-[#6A3683]' : ''
              } ${isCurrentPlan ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {ctaText}
            </Button>
          </div>
        )}

        {/* Features List */}
        {features.length > 0 && (
          <ul className="space-y-3 flex-grow">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <div className={`
                  mr-3 mt-1 w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0
                  ${isPremium 
                    ? 'bg-[#6A3683] border border-[#FFD700]/60 text-[#FFD700]' 
                    : 'bg-[#6A3683] text-[#FBFBFE]'
                  }
                `}>
                  <Check className="h-3 w-3" />
                </div>
                <span className={`
                  text-sm leading-relaxed
                  ${isFree 
                    ? 'text-[#FBFBFE]/60' 
                    : 'text-[#FBFBFE]'
                  }
                `}>
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
